/**
 * Public landing after a caregiver clicks Yes/No in a shift-blast email.
 * URL:  /broadcast/respond?token=…&answer=yes|no
 *
 * The token is single-use and tied to one caregiver+broadcast row on the
 * backend. First Yes wins for `auto_first_yes` shifts.
 */
import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, AlertCircle, Clock, HelpCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BroadcastRespond() {
    const [params] = useSearchParams();
    const token = params.get("token");
    const answer = params.get("answer");

    const [state, setState] = useState({ loading: true, error: null, data: null });

    useEffect(() => {
        if (!token || !answer) {
            setState({ loading: false, error: "Missing token or answer.", data: null });
            return;
        }
        fetch(`${API}/api/broadcasts/respond?token=${encodeURIComponent(token)}&answer=${answer}`)
            .then(async (r) => {
                if (!r.ok) {
                    const j = await r.json().catch(() => ({}));
                    throw new Error(j.detail || r.statusText);
                }
                return r.json();
            })
            .then((d) => setState({ loading: false, error: null, data: d }))
            .catch((e) => setState({ loading: false, error: e.message, data: null }));
    }, [token, answer]);

    let icon, tone, headline, body;
    if (state.loading) {
        icon = <Clock className="w-14 h-14 text-stone-400 animate-pulse" />;
        tone = "border-stone-200";
        headline = "Recording your response…";
        body = "Hang tight.";
    } else if (state.error) {
        icon = <AlertCircle className="w-14 h-14 text-amber-600" />;
        tone = "border-amber-200 bg-amber-50";
        headline = "Link expired or invalid";
        body = state.error;
    } else {
        const d = state.data;
        const rate = d.hourly_rate_cents ? `$${(d.hourly_rate_cents / 100).toFixed(2)}/hour` : "";
        switch (d.status) {
            case "won_shift":
                icon = <CheckCircle2 className="w-14 h-14 text-emerald-600" />;
                tone = "border-emerald-200 bg-emerald-50";
                headline = "You've got the shift!";
                body = `You claimed "${d.shift_title}" in ${d.zip} at ${rate}. Our team will text you the coordinator's number within a few minutes.`;
                break;
            case "yes_recorded_admin_will_pick":
                icon = <CheckCircle2 className="w-14 h-14 text-emerald-600" />;
                tone = "border-emerald-200 bg-emerald-50";
                headline = "Yes recorded — thank you!";
                body = `We've noted you're available for "${d.shift_title}" (${d.zip}, ${rate}). The coordinator will confirm within the collection window and text the winner.`;
                break;
            case "window_closed_admin_will_pick":
                icon = <Clock className="w-14 h-14 text-stone-500" />;
                tone = "border-stone-200 bg-stone-50";
                headline = "Yes noted — but the 5-min window closed";
                body = "We'll still consider you as a backup, but the coordinator may have already selected a caregiver. Watch for their text.";
                break;
            case "already_filled":
                icon = <XCircle className="w-14 h-14 text-stone-500" />;
                tone = "border-stone-200 bg-stone-50";
                headline = "Someone else got there first";
                body = `Another caregiver already took "${d.shift_title}". Thanks for jumping — we'll email you the next one.`;
                break;
            case "recorded_no":
                icon = <CheckCircle2 className="w-14 h-14 text-stone-500" />;
                tone = "border-stone-200 bg-stone-50";
                headline = "Got it, thanks for the quick reply";
                body = `We'll take "${d.shift_title}" off your list. You'll still get future open-shift emails.`;
                break;
            default:
                icon = <HelpCircle className="w-14 h-14 text-stone-400" />;
                tone = "border-stone-200";
                headline = "Response recorded";
                body = "Thanks — check your dashboard for details.";
        }
    }

    return (
        <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6 py-16">
            <div className={`max-w-lg w-full bg-white border-2 rounded-3xl p-10 text-center ${tone}`} data-testid="broadcast-respond-card">
                <div className="flex justify-center mb-6">{icon}</div>
                <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-stone-500 mb-3">
                    Infini Health<span className="text-emerald-700">Care</span> · CareCo 24
                </div>
                <h1 className="font-outfit font-semibold text-3xl text-stone-900 leading-tight tracking-tight">
                    {headline}
                </h1>
                <p className="mt-4 text-stone-600 font-dmsans leading-relaxed" data-testid="broadcast-respond-body">
                    {body}
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-2 justify-center">
                    <Link
                        to="/"
                        data-testid="broadcast-respond-home-btn"
                        className="inline-block px-6 py-3 rounded-full bg-stone-900 hover:bg-stone-800 text-white text-sm font-outfit font-medium"
                    >
                        Back to CareCo 24
                    </Link>
                    <Link
                        to="/auth?mode=login"
                        className="inline-block px-6 py-3 rounded-full bg-white border border-stone-200 text-stone-800 hover:bg-stone-50 text-sm font-outfit font-medium"
                    >
                        Sign in to your caregiver profile
                    </Link>
                </div>
            </div>
        </div>
    );
}
